/**
 * Quality Co-Pilot data-request API service.
 *
 * Merlin (the backend agent) can file "data requests" asking sales-site staff
 * for information; answers come back asynchronously. This module wraps the
 * door-scoped endpoints under `/api/quality-copilot/data-requests` that the
 * Quality Co-Pilot page uses to list requests, answer them in-portal, nudge
 * (request again), dismiss, and mark answers as seen.
 *
 * Uses the shared axios instance (services/api.ts) like qualityCopilot.ts.
 */
import api from './api';

const BASE = '/quality-copilot';

/** 'proposed' is one of Merlin's drafts: nothing has been sent, and it waits
 *  on Jess to send it or throw it away (owner ruling, review bz2bb6). */
export type DataRequestStatus =
  | 'proposed'
  | 'open'
  | 'answered'
  | 'closed'
  | 'cancelled';

/** One file attached to a data-request response. */
export interface DataRequestAttachment {
  id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  gs_uri: string;
  created_at: string;
}

/** One response (answer) recorded against a data request. */
export interface DataRequestResponse {
  id: string;
  channel: string;
  responder_identifier: string;
  body_text: string;
  created_at: string;
  attachments: DataRequestAttachment[];
}

/** One requester→recipient follow-up message in a request's thread. */
export interface DataRequestMessage {
  id: string;
  author: string;
  author_label: string;
  body_text: string;
  created_at: string;
}

/** Who a request was addressed to (person / team / everyone). */
export type DataRequestAudience = 'user' | 'team' | 'company';

/** Where an ask stands on one medium (kanban 31f2809d). */
export type AskDeliveryChannel = 'email' | 'widget' | 'slack' | 'sms';
export type AskDeliveryState = 'sent' | 'sandbox' | 'failed' | 'pending';
export interface DoorAskDeliveryRead {
  channel: AskDeliveryChannel;
  state: AskDeliveryState;
  at: string | null;
  detail: string | null;
}

/** A data request as returned by the door router (DoorDataRequestRead). */
export type AskKind = 'ask' | 'pass_along';

/** Where a pass-along stands with the hand-off curator (kanban 196e9d9b):
 *  pending = held back until Merlin's check (live gate); held = Merlin judged
 *  the desk already has it (Send anyway offered); sent = it went out
 *  (`checked` says whether the verdict is in); failed / superseded = the
 *  release failed or the item closed first. */
export interface DoorAskHandoffRead {
  sweep_item_id: string;
  state: 'pending' | 'held' | 'sent' | 'failed' | 'superseded';
  mode: string;
  checked: boolean;
  verdict?: string | null;
  recommendation?: string | null;
  known_points?: string[];
  note?: string | null;
  detail?: string | null;
  curated_at?: string | null;
  updated_at?: string | null;
}

export interface DoorDataRequestRead {
  id: string;
  conversation_id: string | null;
  /** The INV case the ask belongs to (birth-stamped, or adopted when its
   *  conversation was attached to a case). Older backends omit it. */
  investigation_id?: string | null;
  quality_issue_id: string | null;
  target_name: string;
  target_email: string | null;
  audience: DataRequestAudience;
  audience_key: string | null;
  broadcast_group_id: string | null;
  /** Asks born on a thread's Do next (kanban d3ec3001): the thread and the
   *  recommended_action step they serve, how the recipient was chosen
   *  ('named', 'role:sales_rep', 'team:<key>', 'customer_relay'), and — on
   *  a row stood down because another recipient answered — who answered.
   *  Older backends omit them. */
  thread_id?: string | null;
  step_no?: number | null;
  /** The case's outstanding item the ask was sent for (kanban 52a4147a). */
  task_id?: string | null;
  recipient_source?: string | null;
  peer_answered_by?: string | null;
  /** 'ask' (a question the sender waits on) or 'pass_along' (one piece of the
   *  item handed to the desk that owns it; sent once, nobody waits) — kanban
   *  196e9d9b. Older backends omit it (an ask). */
  kind?: AskKind;
  /** A pass-along's standing with Merlin's "already known?" check; null for
   *  an ask or a pass-along sent with the gate off. Older backends omit it. */
  handoff?: DoorAskHandoffRead | null;
  question_title: string;
  /** Markdown body of the question Merlin asked. */
  question_body: string;
  context_summary: string | null;
  status: DataRequestStatus;
  answered_at: string | null;
  /** True when an answer arrived that the door user has not viewed yet. */
  answer_unseen: boolean;
  /** True once the target marked the item done in their widget. */
  recipient_done: boolean;
  followup_count: number;
  last_followup_at?: string | null;
  next_followup_at: string | null;
  /** When the ask went out (a draft's approval, else its birth); null while a draft. */
  sent_at?: string | null;
  created_at: string;
  responses: DataRequestResponse[];
  /** Requester follow-ups (interleave with responses to form the thread). */
  messages: DataRequestMessage[];
  /** Per-medium delivery (empty for a draft). Older backends omit it. */
  deliveries?: DoorAskDeliveryRead[];
  /** Jess's own words when he dismissed or discarded it (kanban 59a1ee04);
   *  null on a plain dismiss. Older backends omit it. */
  dismissed_note?: string | null;
  /** Who ended the ask and with what (kanban 101a9853): `closed_in_chat`
   *  when the user told Merlin the answer and Merlin closed it; `closed_note`
   *  is that answer. Older backends omit them. */
  closed_at?: string | null;
  closed_by_label?: string | null;
  closed_note?: string | null;
  closed_in_chat?: boolean;
}

/** List the door's data requests, optionally scoped to one conversation. */
export async function listDataRequests(
  conversationId?: string,
): Promise<DoorDataRequestRead[]> {
  const params: Record<string, string> = {};
  if (conversationId) params.conversation_id = conversationId;
  const { data } = await api.get<DoorDataRequestRead[]>(
    `${BASE}/data-requests`,
    { params },
  );
  return data;
}

/** Answer an open request myself (in-portal). 409 if the request is terminal. */
export async function answerDataRequest(
  requestId: string,
  bodyText: string,
): Promise<DoorDataRequestRead> {
  const { data } = await api.post<DoorDataRequestRead>(
    `${BASE}/data-requests/${requestId}/answer`,
    { body_text: bodyText },
  );
  return data;
}

/** Reply into a request's thread. 409 if the request is terminal. */
export async function messageDataRequest(
  requestId: string,
  bodyText: string,
): Promise<DoorDataRequestRead> {
  const { data } = await api.post<DoorDataRequestRead>(
    `${BASE}/data-requests/${requestId}/message`,
    { body_text: bodyText },
  );
  return data;
}

/** Queue a follow-up nudge to the target. 409 unless the request is open. */
export async function nudgeDataRequest(
  requestId: string,
): Promise<DoorDataRequestRead> {
  const { data } = await api.post<DoorDataRequestRead>(
    `${BASE}/data-requests/${requestId}/nudge`,
  );
  return data;
}

/** Dismiss (cancel) a request. 409 if the request is already terminal.
 *  ``note``: Jess's own words on why (kanban 59a1ee04) — kept on the row and
 *  taught to Merlin; null is the plain one-click dismiss. */
export async function dismissDataRequest(
  requestId: string,
  reason?: string,
  note: string | null = null,
): Promise<DoorDataRequestRead> {
  const { data } = await api.post<DoorDataRequestRead>(
    `${BASE}/data-requests/${requestId}/dismiss`,
    { ...(reason ? { reason } : {}), ...(note ? { note } : {}) },
  );
  return data;
}

/** Send one of Merlin's drafts, optionally reworded first. PROPOSED -> OPEN. */
export async function approveDataRequest(
  requestId: string,
  edits: {
    question_title?: string;
    question_body?: string;
    context_summary?: string;
  } = {},
): Promise<DoorDataRequestRead> {
  const { data } = await api.post<DoorDataRequestRead>(
    `${BASE}/data-requests/${requestId}/approve`,
    edits,
  );
  return data;
}

/** Throw one of Merlin's drafts away. Nothing was ever sent. ``note`` as on
 *  dismissDataRequest. */
export async function discardDataRequest(
  requestId: string,
  reason?: string,
  note: string | null = null,
): Promise<DoorDataRequestRead> {
  const { data } = await api.post<DoorDataRequestRead>(
    `${BASE}/data-requests/${requestId}/discard`,
    { ...(reason ? { reason } : {}), ...(note ? { note } : {}) },
  );
  return data;
}

/** Mark a request's answer as seen (clears the NEW badge server-side). */
export async function markDataRequestSeen(
  requestId: string,
): Promise<DoorDataRequestRead> {
  const { data } = await api.post<DoorDataRequestRead>(
    `${BASE}/data-requests/${requestId}/seen`,
  );
  return data;
}

/** File an ask that has no case on an open case. 409 when it already has
 *  one or the case is closed; 404 when either is not this door's. */
export async function attachDataRequest(
  requestId: string,
  investigationId: string,
): Promise<DoorDataRequestRead> {
  const { data } = await api.post<DoorDataRequestRead>(
    `${BASE}/data-requests/${requestId}/attach`,
    { investigation_id: investigationId },
  );
  return data;
}

/* ---------------------------------------------------------------- */
/* Asks from a thread's Do next (kanban d3ec3001)                     */
/* ---------------------------------------------------------------- */

export interface DoorAskPersonRead {
  name: string;
  email: string | null;
}

export interface DoorAskTeamMemberRead {
  name: string;
  /** Why the directory cannot deliver to them. */
  reason: string;
}

export interface DoorAskTeamRead {
  /** `team:<key>` is the token the send call takes. */
  key: string;
  label: string;
  /** "Curated team" / "Org chart: reports to Laura Caswell" / "Org chart: member of". */
  source: string;
  members: DoorAskPersonRead[];
  unresolved: DoorAskTeamMemberRead[];
  /** Every spelling a desk may use for it ("accounting", "a/r", "the lab"):
   *  a function desk (`fn_*`, kanban 196e9d9b). Older backends omit it. */
  aliases?: string[];
  /** The account role this desk stands in for when nobody fills it. */
  covers_role?: 'sales_rep' | 'cs_rep' | null;
}

export interface DoorAskRoleRead {
  role: 'sales_rep' | 'cs_rep';
  token: string;
  label: string;
  customer_no: string | null;
  resolved_name: string | null;
  resolved_email: string | null;
  detail: string | null;
  available: boolean;
  reason: string | null;
}

/** Who an ask on one thread can go to. */
export interface DoorAskTargetsRead {
  roles: DoorAskRoleRead[];
  teams: DoorAskTeamRead[];
  staff: DoorAskPersonRead[];
  /** Who a "customer" ask relays through (the account's sales rep), if anyone. */
  customer_relay: DoorAskPersonRead | null;
}

export async function getThreadAskTargets(threadId: string): Promise<DoorAskTargetsRead> {
  const { data } = await api.get<DoorAskTargetsRead>(`${BASE}/threads/${threadId}/ask-targets`);
  return data;
}

export interface ThreadAskSendBody {
  step_no: number | null;
  /** The case's outstanding item the ask is for, when sent from its row. */
  task_id?: string | null;
  desk: string;
  /** Desk parts and picker tokens: names, `role:sales_rep`, `team:<key>`, `customer`. */
  recipients: string[];
  question_title: string;
  question_body: string;
  context_summary?: string | null;
  /** Merlin's chat drafts this send stands for (kanban 31f2809d): those
   *  addressed to a person kept on the send are approved, not duplicated. */
  draft_ids?: string[];
  /** 'ask' (default) or 'pass_along' (kanban 196e9d9b). */
  kind?: AskKind;
}

export interface DoorThreadAskSendRead {
  sent: DoorDataRequestRead[];
  discarded: DoorDataRequestRead[];
}

/** Send a pass-along now, past Merlin's "already known" hold or before its
 *  verdict (kanban 196e9d9b). 409 once it has gone out. */
export async function sendAskAnyway(requestId: string): Promise<DoorDataRequestRead> {
  const { data } = await api.post<DoorDataRequestRead>(`${BASE}/asks/${requestId}/send-anyway`);
  return data;
}

/** What the person would receive if the ask were sent as it stands
 *  (kanban db44d231): the assembled email, built by the same code that
 *  sends it. The modal shows it so what Jess reads is what goes out. */
export interface AskPreviewBody {
  step_no: number | null;
  task_id?: string | null;
  desk: string;
  recipients: string[];
  question_title: string;
  question_body: string;
  kind?: AskKind;
}

export interface DoorAskPreviewRead {
  subject: string;
  /** The email as the person reads it, up to "Thanks!"; Merlin's signature follows. */
  body_text: string;
  /** Who it is addressed to, as the directory names them. */
  to: string[];
  /** One email to everyone on it (two or more people). */
  group: boolean;
  /** The other media it also goes out on ('slack', 'sms'). */
  also_on: string[];
  /** Recipient tokens the directory could not resolve (previewed by name). */
  unresolved: string[];
}

export async function previewThreadAsk(threadId: string, body: AskPreviewBody): Promise<DoorAskPreviewRead> {
  const { data } = await api.post<DoorAskPreviewRead>(`${BASE}/threads/${threadId}/asks/preview`, body);
  return data;
}

export async function previewInvestigationAsk(
  investigationId: string,
  body: AskPreviewBody,
): Promise<DoorAskPreviewRead> {
  const { data } = await api.post<DoorAskPreviewRead>(`${BASE}/investigations/${investigationId}/asks/preview`, body);
  return data;
}

/** Send the ask for one Do next desk. 400 when a recipient does not resolve. */
export async function sendThreadAsk(
  threadId: string,
  body: ThreadAskSendBody,
): Promise<DoorThreadAskSendRead> {
  const { data } = await api.post<DoorThreadAskSendRead>(
    `${BASE}/threads/${threadId}/asks/send`,
    body,
  );
  return data;
}

/** Who an ask on one case can go to: the thread's account when the case was
 *  opened from one, else the case's own keys (kanban 52a4147a). */
export async function getInvestigationAskTargets(investigationId: string): Promise<DoorAskTargetsRead> {
  const { data } = await api.get<DoorAskTargetsRead>(
    `${BASE}/investigations/${investigationId}/ask-targets`,
  );
  return data;
}

export type CaseAskSendBody = ThreadAskSendBody;

/** Send the ask for one of the case's outstanding items. 400 when a recipient
 *  does not resolve; 404 when the item is not on this case. */
export async function sendInvestigationAsk(
  investigationId: string,
  body: CaseAskSendBody,
): Promise<DoorThreadAskSendRead> {
  const { data } = await api.post<DoorThreadAskSendRead>(
    `${BASE}/investigations/${investigationId}/asks/send`,
    body,
  );
  return data;
}
